import Link from 'next/link';
import Profile from '@/assets/icons/common/avatar.svg';
import After10th from '@/assets/icons/course/after-10th.png';
import After12th from '@/assets/icons/course/after-12th.png';
import Certification from '@/assets/icons/course/certification.png';
import Diploma from '@/assets/icons/course/diploma.png';
import Masters from '@/assets/icons/course/masters.png';
import Phd from '@/assets/icons/course/phd.png';
import AdmissionIcon from '@/assets/icons/modals/admission-icon.png';
import CollegePredictorIcon from '@/assets/icons/modals/college-predictor.png';
import EducationLoadIcon from '@/assets/icons/modals/education-loan.png';
import PlacementIcon from '@/assets/icons/modals/placement.png';
import ReviewsIcon from '@/assets/icons/modals/review.png';
import ScholarshipIcon from '@/assets/icons/modals/scholarship.png';
import ExamsImage from '@/assets/logo/exams.webp';
import FindCollegeImage from '@/assets/logo/find-college.webp';
import StudyAbroadImage from '@/assets/logo/study-abroad.webp';
import { formatCost } from '@/utils/formatItems';
import { generateNumberBasedOnId } from '@/utils/generateCount';
import { Globe, GraduationCap, Mail, MapPin, Phone, UserRound } from 'lucide-react';

export const cardData = (course: any, courseData: any) => {
  return [
    {
      title: 'Find Colleges',
      subHeading: 'Discover 15000+ colleges via preferences',
      imageSrc: FindCollegeImage,
      linkText: `Explore Top ${course?.abbreviation} Colleges in India`,
      linkUrl: `${course?.course_url?.replace(/\s+/g, '-').toLowerCase()}-colleges-in-india`,
      content: (
        <div className="mt-1 text-left text-xs">
          <ul className="flex flex-wrap justify-between gap-y-1.5">
            {courseData?.cities &&
              courseData?.cities?.length > 0 &&
              courseData?.cities?.slice(0, 6)?.map((city: any) => {
                const courseUrl = course?.course_url?.replace(/\s+/g, '-')?.toLowerCase();
                const cityUrl = city?.city_url?.replace(/\s+/g, '-')?.toLowerCase();

                return (
                  <li key={city?.id} className="capitalize">
                    <Link
                      href={`/${courseUrl}-colleges-in-${cityUrl}`}
                      className="rounded-full bg-primary-white p-1 px-2 py-0.5"
                    >
                      Top {course?.abbreviation} colleges in {city?.city_name}
                    </Link>
                  </li>
                );
              })}
          </ul>
        </div>
      ),
    },
    {
      title: 'Exams',
      subHeading: 'Know more about your exams',
      imageSrc: ExamsImage,
      linkText: `Check all ${courseData?.abbreviation ?? 'available'} exams in India`,
      linkUrl: `/exams/${courseData?.streams?.[0]?.stream_name?.replace(/\s+/g, '-')?.toLowerCase()}`,
      content: (
        <div className="flex flex-wrap gap-x-2 gap-y-1.5 text-center">
          {courseData?.exams && courseData?.exams?.length > 0 ? (
            courseData?.exams?.slice(0, 10)?.map((exam: any) => {
              const examNavbar = exam?.exam_navbars?.[0]?.title?.replace(/\s+/g, '-')?.toLowerCase() ?? 'exam-info';
              return (
                <Link href={`/exams/${exam?.exam_url}/${examNavbar}`} key={exam?.id}>
                  <p className="w-fit rounded-full bg-primary-white px-2 py-0.5 text-start text-xs">
                    {exam?.abbreviation ?? exam?.exam_name}
                  </p>
                </Link>
              );
            })
          ) : (
            <p className="text-center text-xs text-gray-500">No exam is available for the related course.</p>
          )}
        </div>
      ),
    },
    {
      title: 'Study Abroad',
      subHeading: 'Discover colleges across the globe',
      imageSrc: StudyAbroadImage,
      linkText: 'Study Abroad',
      linkUrl: '/study-abroad',
      content: (
        <div className="mt-1 flex flex-wrap gap-2 text-center text-xs capitalize">
          {courseData?.countries &&
            courseData?.countries?.length > 0 &&
            courseData?.countries
              ?.filter((country: any) => country?.country_name?.toLowerCase() !== 'india') // Filter out India
              ?.slice(0, 15)
              ?.map((country: any) => {
                const courseUrl = course?.course_url?.replace(/\s+/g, '-')?.toLowerCase();

                const countryUrl = country?.country_url?.includes('study-in-')
                  ? country.country_url.replace('study-in-', 'colleges-in-').replace(/\s+/g, '-').toLowerCase()
                  : '#';

                return (
                  <Link
                    href={countryUrl !== '#' ? `/${courseUrl}-${countryUrl}` : '#'}
                    key={country?.id}
                    className="w-fit rounded-full bg-primary-white px-2 py-0.5 text-start text-xs"
                  >
                    {country?.abbreviation ?? country?.country_name}
                  </Link>
                );
              })}
        </div>
      ),
    },
  ];
};

export const targetCountries = [
  { id: 1, country_name: 'UNITED KINGDOM', left: 38, right: 12 },
  { id: 2, country_name: 'UNITED STATES', left: 10, right: 20 },
  { id: 3, country_name: 'UNITED ARAB EMIRATES', left: 52, right: 30 },
  { id: 4, country_name: 'UZBEKISTAN', left: 56, right: 16 },
  { id: 5, country_name: 'KAZAKHSTAN', left: 58, right: 10 },
  { id: 6, country_name: 'CANADA', left: 14, right: 8 },
  { id: 7, country_name: 'AUSTRALIA', left: 85, right: 65 },
  { id: 8, country_name: 'GERMANY', left: 38, right: 35 },
  { id: 9, country_name: 'IRELAND', left: 35, right: 19 },
  { id: 10, country_name: 'NEW ZEALAND', left: 91, right: 80 },
  { id: 11, country_name: 'NETHERLANDS', left: 41, right: 25 },
  { id: 12, country_name: 'SINGAPORE', left: 75, right: 53 },
  { id: 13, country_name: 'SWITZERLAND', left: 45, right: 40 },
];

export const StudyAbroadCountData = (country: any) => [
  {
    id: 1,
    label: 'Exams',
    value: `${country?.exams?.count > 0 ? country?.exams?.count : generateNumberBasedOnId(country?.id, 30, 50)}+`,
    icon: Profile,
  },
  {
    id: 2,
    label: 'Scholarships',
    value: `${country?.scholarships?.count > 0 ? country?.scholarships?.count : generateNumberBasedOnId(country?.id, 30, 50)}+`,
    icon: Profile,
  },
  {
    id: 3,
    label: 'Avg. Living Costs',
    value: `${country?.average_cost_living ? formatCost(country?.average_cost_living) : 'N/A'} USD/yr`,
    icon: Profile,
  },
  {
    id: 4,
    label: 'Courses',
    value: `${country?.courses?.count > 0 ? country?.courses?.count : generateNumberBasedOnId(country?.id, 30, 50)}+`,
    icon: Profile,
  },
];

export const linksData = (country: any) => [
  {
    id: 1,
    label: `Why Choose ${country?.country_name}`,
    url: '#why-choose-usa',
  },
  { id: 2, label: 'SOP/LOR Guidance', url: '#sop-lor-guidance' },
  {
    id: 3,
    label: `Careers After Studying ${country?.country_name}`,
    url: '#careers-after-usa',
  },
  // { id: 4, label: "Sample Paper & More", url: "#sample-paper-more" },
];

export const courseIconMap: any = {
  Diploma: {
    displayName: 'Courses After 10th',
    icon: After10th,
    label: 'Applicable for Diploma courses & Certification courses',
    color: '#F2393699',
  },
  'After 10+2 Courses': {
    displayName: 'Courses After 12th',
    icon: After12th,
    label: 'Applicable for Degree, Diploma & Certification courses',
    color: '#45C646A6',
  },
  PGDM: {
    displayName: 'Diploma Courses',
    icon: Diploma,
    label: 'Applicable for Diploma courses',
    color: '#008EFFA6',
  },
  Certification: {
    displayName: 'Certification Courses',
    icon: Certification,
    label: 'Applicable for Certification courses',
    color: '#C136F2A6',
  },
  'Masters Degree / Post Graduation Courses': {
    displayName: 'Masters/PG Courses',
    icon: Masters,
    label: 'Applicable for Diploma & Certification courses',
    color: '#E0AF51A6',
  },
  PhD: {
    displayName: 'Ph.D. Programs',
    icon: Phd,
    label: 'Applicable for Degree courses',
    color: '#FF3B74A6',
  },
};

export const services = [
  { name: 'Admission', icon: AdmissionIcon, alt: 'Admission service' },
  { name: 'Placement', icon: PlacementIcon, alt: 'Placement service' },
  { name: 'Review', icon: ReviewsIcon, alt: 'Review service' },
  {
    name: 'Education Loan',
    icon: EducationLoadIcon,
    alt: 'Education Loan service',
  },
  { name: 'Scholarship', icon: ScholarshipIcon, alt: 'Scholarship service' },
  {
    name: 'College Predictor',
    icon: CollegePredictorIcon,
    alt: 'College Predictor service',
  },
];

export const formFields = [
  {
    label: 'Full Name',
    name: 'name',
    icon: <UserRound size={16} />,
    placeholder: 'Enter Full Name',
    type: 'text',
  },
  {
    label: 'Email Address',
    name: 'email',
    icon: <Mail size={16} />,
    placeholder: 'Enter Email Address',
    type: 'email',
  },
  {
    label: 'Phone Number',
    name: 'phone_number',
    icon: <Phone size={16} />,
    placeholder: 'Enter Phone Number',
    type: 'tel',
  },
  {
    label: 'City you live in',
    name: 'city',
    icon: <MapPin size={16} />,
    placeholder: 'Enter City',
    type: 'text',
  },
  {
    label: 'Course',
    name: 'course',
    icon: <GraduationCap size={16} />,
    placeholder: 'Select course',
    type: 'text',
  },
  {
    label: 'Country Applying for',
    name: 'country',
    icon: <Globe size={16} />,
    placeholder: 'Select country',
    type: 'array',
  },
];

export const reviewFormFields = [
  {
    label: 'Full Name',
    name: 'name',
    icon: <UserRound size={16} />,
    placeholder: 'Enter Full Name',
    type: 'text',
  },
  {
    label: 'Email Address',
    name: 'email',
    icon: <Mail size={16} />,
    placeholder: 'Enter Email Address',
    type: 'email',
  },
  {
    label: 'Phone Number',
    name: 'phone_number',
    icon: <Phone size={16} />,
    placeholder: 'Enter Phone Number',
    type: 'tel',
  },
  {
    label: 'Country',
    name: 'country',
    icon: <Globe size={16} />,
    placeholder: 'Select country',
    type: 'text',
  },
];
